<script setup lang="ts">
import { defineProps, computed } from 'vue';

import DefaultChip from './DefaultChip.vue';

const props = defineProps<{
  segment: any;
}>();

const pricingRules = computed(() => {
  const rules = props.segment?.rules || [];

  const findRuleBySlug = (slug: string) => rules.find((r: any) => r.slug === slug);

  return {
    netPrice: findRuleBySlug('cena-netto-dla-klienta-wszystkie-technologie'),
    grossPrice: findRuleBySlug('cena-brutto-dla-klienta-wszystkie-technologie'),
    subsidy: findRuleBySlug('dotacja'),
    clientContribution: findRuleBySlug('kwota-po-dotacji-wklad-wlasny-klienta'),
    thermalDeduction: findRuleBySlug('ulga-termomodernizacyjna-do-odliczenia'),
  };
});

const paymentRules = computed(() => {
  const rules = props.segment?.rules || [];

  const findRuleBySlug = (slug: string) => rules.find((r: any) => r.slug === slug);

  return {
    vatPayment: findRuleBySlug('platnosc-1-vat-od-pierwszej-transzy-dotacji-klient'),
    firstSubsidy: findRuleBySlug('platnosc-2-pierwsza-transza-dotacji-35percent-wfos'),
    secondSubsidy: findRuleBySlug('platnosc-3-druga-transza-dotacji-wfos-po-montazu-i-oplaceniu-faktury-koncowej'),
    clientPaymentAfterInstallation: findRuleBySlug('platnosc-4-wklad-wlasny-klienta-klient-po-montazu'),
  };
});

const financingOptions = computed(() => {
  const rules = props.segment?.rules || [];

  const findRuleBySlug = (slug: string) => rules.find((r: any) => r.slug === slug);

  return {
    cashPaymentOption1: rules.find((r: any) => r.slug === 'wariant-1-klient-placi-gotowka' && r.order === 10),
    cashPaymentOption2: rules.find((r: any) => r.slug === 'wariant-1-klient-placi-gotowka' && r.order === 14),
    trifyInterestRate: findRuleBySlug('wariant-2-rata-trify-odsetkowa'),
    trifyLastPayment: findRuleBySlug('wariant-2-rata-trify-ostatnia'),
    externalFinancing: findRuleBySlug('wariant-2-rata-finansowanie-zewnetrzne'),
  };
});

const groupRules = computed(() => {
  const groups = props.segment?.groups || [];
  const allGroupRules = groups.flatMap((group: any) => group.rules || []);

  const findGroupRuleBySlug = (slug: string) => allGroupRules.find((r: any) => r.slug === slug);

  return {
    loanAmount: findGroupRuleBySlug('kwota-kredytu-netto-do-wyplaty'),
    installmentCount: findGroupRuleBySlug('liczba-rat'),
    interestRate: findGroupRuleBySlug('oprocentowanie-nominalne-w-skali-roku'),
  };
});

</script>

<template>
  <div class="space-y-6">
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div
        class="size-lf-stretch p-4 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 inline-flex flex-col justify-start items-start gap-4 overflow-hidden"
      >
        <h3 class="justify-start text-gray-900 text-base font-bold leading-normal">Podsumowanie ceny</h3>
        <div class="self-stretch flex-1 flex flex-col justify-between items-start">
          <div class="self-stretch inline-flex justify-between items-center">
            <p class="gray-iron-700 text-sm">
              {{ pricingRules.netPrice?.name || 'Cena netto' }}
            </p>
            <DefaultChip class="text-sm" variant="gray">
              {{ pricingRules.netPrice?.eval || '-' }}
              {{ pricingRules.netPrice?.unit }}
            </DefaultChip>
          </div>

          <div class="self-stretch inline-flex justify-between items-center">
            <p class="gray-iron-700 text-sm">
              {{ pricingRules.grossPrice?.name || 'Cena brutto' }}
            </p>
            <DefaultChip class="text-sm" variant="yellow">
              {{ pricingRules.grossPrice?.eval || '-' }}
              {{ pricingRules.grossPrice?.unit }}
            </DefaultChip>
          </div>

          <div class="self-stretch h-px bg-zinc-200"></div>

          <div class="self-stretch inline-flex justify-between items-center">
            <p class="gray-iron-700 text-sm">
              {{ pricingRules.subsidy?.name }}
            </p>
            <DefaultChip class="text-sm" variant="green">
              {{ pricingRules.subsidy?.eval || '-' }}
              {{ pricingRules.subsidy?.unit }}
            </DefaultChip>
          </div>

          <div class="self-stretch inline-flex justify-between items-center">
            <p class="gray-iron-700 text-sm">
              {{ pricingRules.clientContribution?.name || 'Kwota po dotacji (wkład klienta)' }}
            </p>
            <DefaultChip class="text-sm" variant="green">
              {{ pricingRules.clientContribution?.eval || '-' }}
              {{ pricingRules.clientContribution?.unit }}
            </DefaultChip>
          </div>

          <div class="self-stretch inline-flex justify-between items-center">
            <p class="gray-iron-700 text-sm">
              {{ pricingRules.thermalDeduction?.name || 'Ulga termomodernizacyjna do odliczenia' }}
            </p>
            <DefaultChip variant="blue">
              {{ pricingRules.thermalDeduction?.eval || '-' }}
              {{ pricingRules.thermalDeduction?.unit}}
            </DefaultChip>
          </div>
        </div>
      </div>

      <div
        class="inline-flex flex-col items-start justify-start gap-3 self-stretch rounded-[10px] bg-white p-4 outline outline-1 outline-offset-[-1px] outline-zinc-200"
      >
        <h3 class="text-base font-bold text-gray-900">Proces płatności</h3>
        <div
          class="inline-flex flex-col items-start justify-start gap-2 self-stretch rounded-lg bg-neutral-50 p-3 outline outline-1 outline-offset-[-1px] outline-zinc-200"
        >
          <div class="text-base font-normal text-zinc-700">
            Pierwsza płatność - zaliczka po otrzymaniu pozytywnego rozpatrzenia wniosku
          </div>

          <div
            class="inline-flex items-center justify-between self-stretch rounded-lg bg-yellow-100 p-3"
          >
            <div class="size- inline-flex flex-col items-start justify-center">
              <div class="justify-start text-base font-medium leading-normal text-zinc-900">
                VAT (od pierwszej transzy dotacji)
              </div>
              <div class="justify-start text-sm font-normal leading-tight text-zinc-700">
                Klient na podstawie faktury
              </div>
            </div>
            <div class="justify-start text-lg font-bold leading-7 text-yellow-700">
              {{ paymentRules.vatPayment?.eval || '-' }} {{ paymentRules.vatPayment?.unit }}
            </div>
          </div>
        </div>
        <div
          class="inline-flex flex-col items-start justify-start gap-2 self-stretch rounded-lg bg-neutral-50 p-3 outline outline-1 outline-offset-[-1px] outline-zinc-200"
        >
          <div class="text-base font-normal text-zinc-700">Po opłaceniu VAT-u przez klienta</div>

          <div
            class="inline-flex items-center justify-between self-stretch rounded-lg bg-yellow-100 p-3"
          >
            <div class="size- inline-flex flex-col items-start justify-center">
              <div class="justify-start text-base font-medium leading-normal text-zinc-900">
                Pierwsza transza dotacji (35%)
              </div>
              <div class="justify-start text-sm font-normal leading-tight text-zinc-700">
                Wypłaca WFOŚiGW
              </div>
            </div>
            <div class="justify-start text-lg font-bold leading-7 text-blue-700">
              {{ paymentRules.firstSubsidy?.eval || '-' }} {{ paymentRules.firstSubsidy?.unit }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div class="self-stretch p-4 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 inline-flex flex-col justify-start items-center gap-4 overflow-hidden">
        <div class="self-stretch p-3 bg-emerald-50 rounded-lg inline-flex justify-between items-center">
          <div class="inline-flex flex-col justify-center items-start">
            <div class="justify-start text-zinc-900 text-base font-medium leading-normal">Wkład własny klienta</div>
            <div class="justify-start text-zinc-700 text-sm font-normal leading-tight">Klient na podstawie faktury</div>
          </div>
          <div class="justify-start text-green-700 text-lg font-bold leading-7">
            {{ groupRules.loanAmount?.eval || paymentRules.clientPaymentAfterInstallation?.eval || '-' }}
            {{ groupRules.loanAmount?.unit || paymentRules.clientPaymentAfterInstallation?.unit || '' }}
          </div>
        </div>
          <h4 class="justify-start text-zinc-700 text-base font-normal leading-normal">Dwie możliwości finansowania</h4>
          <div class="self-stretch inline-flex justify-start items-start gap-4">
            <div class="flex-1 self-stretch inline-flex flex-col justify-start items-center gap-4">
              <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Opcja 1</div>
              <div class="self-stretch flex-1 p-3 bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-center items-center gap-2">
                <div class="w-10 h-10 relative overflow-hidden">
                  <div class="w-7 h-5 left-[6.12px] top-[8.12px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                  <div class="w-7 h-5 left-[11.10px] top-[13.10px] absolute opacity-40 outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                  <div class="w-0.5 h-0 left-[10.86px] top-[23.74px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                  <div class="w-1.5 h-1.5 left-[16.02px] top-[15.14px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                  <div class="w-0.5 h-0 left-[25.84px] top-[13.28px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                </div>
                <div class="justify-start text-zinc-900 text-base font-medium leading-normal">Klient płaci gotówką</div>
              </div>
            </div>
            <div class="flex-1 inline-flex flex-col justify-start items-center gap-4">
              <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Opcja 2</div>
              <div class="self-stretch p-3 bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-start items-start gap-3">
                <div class="self-stretch inline-flex justify-between items-center">
                  <div class="justify-start text-zinc-700 text-base font-normal leading-normal">RRSO</div>
                  <div data-color="Gray" data-icon="False" data-size="md" data-type="Pill outline" class="px-2.5 py-0.5 bg-emerald-50 rounded-2xl outline outline-[1.50px] outline-offset-[-1.50px] outline-emerald-300 flex justify-start items-center">
                    <div class="text-center justify-start text-slate-700 text-sm font-medium leading-tight">
                      {{ groupRules.interestRate?.eval || '-' }}{{ groupRules.interestRate?.unit || '' }}
                    </div>
                  </div>
                </div>
                <div class="self-stretch inline-flex justify-between items-center">
                  <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Liczba miesięcy</div>
                  <div data-color="Gray" data-icon="False" data-size="md" data-type="Pill outline" class="px-2.5 py-0.5 bg-gray-50 rounded-2xl outline outline-[1.50px] outline-offset-[-1.50px] outline-gray-300 flex justify-start items-center">
                    <div class="text-center justify-start text-slate-700 text-sm font-medium leading-tight">
                      {{ Math.floor(Number(groupRules.installmentCount?.eval)) || '-' }}
                    </div>
                  </div>
                </div>
                <div class="self-stretch inline-flex justify-between items-center">
                  <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Rata miesięczna</div>
                  <div data-color="Gray" data-icon="False" data-size="md" data-type="Pill outline" class="px-2.5 py-0.5 bg-blue-50 rounded-2xl outline outline-[1.50px] outline-offset-[-1.50px] outline-blue-300 flex justify-start items-center">
                    <div class="text-center justify-start text-slate-700 text-sm font-medium leading-tight">
                      {{ financingOptions.externalFinancing?.eval || '-' }} {{ financingOptions.externalFinancing?.unit || '' }}
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>

      <div class="self-stretch p-4 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-zinc-200 inline-flex flex-col justify-center items-center gap-3 overflow-hidden">
        <div class="self-stretch p-3 bg-blue-50 rounded-lg inline-flex justify-between items-center">
          <div class="flex-1 inline-flex flex-col justify-center items-start">
            <div class="self-stretch justify-start text-zinc-900 text-base font-medium leading-normal">Druga transza dotacji wypłacona przez WFOŚiGW po montażu i opłaceniu faktury końcowej</div>
            <div class="justify-start text-zinc-700 text-sm font-normal leading-tight">Gotówka / pożyczka pomostowa</div>
          </div>
          <div class="justify-start text-blue-700 text-lg font-bold leading-7">
            {{ paymentRules.secondSubsidy?.eval || '-' }} {{ paymentRules.secondSubsidy?.unit || '' }}
          </div>
        </div>
        <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Dwie możliwości finansowania</div>
        <div class="self-stretch inline-flex justify-start items-start gap-4">
          <div class="flex-1 self-stretch inline-flex flex-col justify-start items-center gap-4">
            <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Opcja 1</div>
            <div class="self-stretch flex-1 p-3 bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-center items-center gap-2">
              <div class="w-10 h-10 relative overflow-hidden">
                <div class="w-7 h-5 left-[6.12px] top-[8.12px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                <div class="w-7 h-5 left-[11.10px] top-[13.10px] absolute opacity-40 outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                <div class="w-0.5 h-0 left-[10.86px] top-[23.74px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                <div class="w-1.5 h-1.5 left-[16.02px] top-[15.14px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
                <div class="w-0.5 h-0 left-[25.84px] top-[13.28px] absolute outline outline-[2.62px] outline-offset-[-1.31px] outline-zinc-500"></div>
              </div>
              <div class="justify-start text-zinc-900 text-base font-medium leading-normal">Klient płaci gotówką</div>
              <div class="self-stretch text-center justify-start text-zinc-700 text-sm font-normal leading-tight">Druga transza dotacji będzie przelana na konto Beneficjenta</div>
            </div>
          </div>
          <div class="flex-1 inline-flex flex-col justify-start items-center gap-4">
            <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Opcja 2</div>
            <div class="self-stretch p-3 bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-start items-start gap-4">
              <div class="self-stretch flex flex-col justify-start items-start gap-2">
                <div class="self-stretch text-center justify-start text-zinc-900 text-base font-medium leading-normal">Uruchamiamy pożyczkę pomostową (Trify), w której klient płaci samą ratę odsetkową bez spłaty kapitału do czasu wypłaty drugiej transzy dotacji z WFOŚiGW </div>
                <div class="self-stretch text-center justify-start text-zinc-700 text-sm font-normal leading-tight">Druga transza dotacji spłaca pożyczkę pomostową i ją zamyka </div>
              </div>
              <div class="self-stretch h-px bg-zinc-200"></div>
              <div class="self-stretch inline-flex justify-between items-center">
                <div class="justify-start text-zinc-700 text-base font-normal leading-normal">Rata miesięczna</div>
                <div data-color="Gray" data-icon="False" data-size="md" data-type="Pill outline" class="px-2.5 py-0.5 bg-blue-50 rounded-2xl outline outline-[1.50px] outline-offset-[-1.50px] outline-blue-300 flex justify-start items-center">
                  <div class="text-center justify-start text-slate-700 text-sm font-medium leading-tight">
                    {{ financingOptions.trifyInterestRate?.eval || '-' }} {{ financingOptions.trifyInterestRate?.unit || '' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="self-stretch flex flex-col justify-start items-start gap-2">
      <div class="self-stretch p-3 bg-emerald-50 rounded-lg inline-flex justify-between items-center">
        <div class="justify-start text-zinc-900 text-base font-medium leading-normal">Do odliczenia ulga termomodernizacyjna w podatku dochodowym</div>
        <div class="justify-start text-green-700 text-xl font-bold leading-loose">
          {{ pricingRules.thermalDeduction?.eval || '-' }} {{ pricingRules.thermalDeduction?.unit || '' }}
        </div>
      </div>

      <div class="self-stretch p-3 bg-yellow-50 rounded-lg inline-flex justify-between items-center">
        <div class="inline-flex flex-col justify-center items-start">
          <div class="justify-start text-zinc-900 text-base font-bold leading-normal">Cena finalna</div>
          <div class="justify-start text-zinc-700 text-sm font-normal leading-tight">Po dotacjach i ulgach brutto</div>
        </div>
        <div class="justify-start text-yellow-700 text-xl font-bold leading-loose">
          {{ groupRules.loanAmount?.eval || pricingRules.clientContribution?.eval || '-' }}
          {{ groupRules.loanAmount?.unit || pricingRules.clientContribution?.unit || '' }}
        </div>
      </div>
    </div>
  </div>
</template>
